import { filter, map, pipe } from "ramda";
import { DropdownOption } from "src/createEntityModal/types";
import { ObjectToArray, toCamelCase } from "src/helpers";
import {
  reportingCurrencyDropdownOptions,
  roundingUnitDropdownOptions,
  roundingUnitEncapDropdownOptions
} from "src/metadata/localOptions";
import { Spread } from "src/spread/model/spread/types";
import {
  MetadataFormKeys,
  MetadataFrontEndConfig,
  MetadataInput,
  MetadataInputOption,
  ModelTemplate,
  ModelTemplateDropdownOption,
  RawMetadataConfig,
  RawMetadataConfigItem
} from "./types";

export const isMetaDataValueAModelTemplate = (
  value: string | ModelTemplate | null
): value is ModelTemplate => {
  return value
    ? (value as ModelTemplate).modelTemplateType !== undefined
    : false;
};

export const transformStringIntoDropdownOption = (value: string) => {
  return {
    value,
    label: value
  } as DropdownOption;
};

export const transformModelTemplateValueIntoDropdownOption = (
  value: ModelTemplate
) => {
  return {
    value,
    label: value.modelTemplateType
  } as ModelTemplateDropdownOption;
};

export const getMetadataFieldsOnAboutPage = (
  rawConfig: RawMetadataConfig
): RawMetadataConfig =>
  filter(
    (configItem: RawMetadataConfigItem) => configItem.isEnabled,
    rawConfig
  );

export const getMetadataFieldsOnAUploadForm = (
  rawConfig: RawMetadataConfig
): RawMetadataConfig =>
  filter(
    (configItem: RawMetadataConfigItem) =>
      configItem.shouldDisplayOnUpload && configItem.isEnabled,
    rawConfig
  );

export const getDropdownOptionFromMetadataValue = (
  value: string | ModelTemplate
): MetadataInputOption => {
  if (isMetaDataValueAModelTemplate(value)) {
    return transformModelTemplateValueIntoDropdownOption(value);
  } else {
    return transformStringIntoDropdownOption(value);
  }
};

export const getDropdownOptionsFromLocalFile = (
  metadataFormKey: string,
  isDbs?: boolean
) => {
  switch (metadataFormKey) {
    case MetadataFormKeys.RoundingUnit:
      if (isDbs) {
        return roundingUnitDropdownOptions;
      } else {
        return roundingUnitEncapDropdownOptions;
      }
    case MetadataFormKeys.SourceCurrency:
      return reportingCurrencyDropdownOptions;
    default:
      return [""];
  }
};

export const getDropdownOptionsFromMetadataValues = (
  values: string[] | ModelTemplate[]
) => {
  return map<any, any>(getDropdownOptionFromMetadataValue, values);
};

export const isTemplateTypeMetadataField = (formKey: string) => {
  return formKey === MetadataFormKeys.TemplateType;
};

export const isCurrencyMetadataField = (formKey: string) => {
  return formKey === MetadataFormKeys.SourceCurrency;
};

export const isDisabledMetadataFieldOnAboutPage = (
  formFieldData: MetadataInput
) => {
  const { formKey } = formFieldData;
  return (
    formKey === MetadataFormKeys.TemplateType ||
    formKey === MetadataFormKeys.QCTeam1 ||
    formKey === MetadataFormKeys.QCTeam2 ||
    formKey === MetadataFormKeys.Language ||
    formFieldData.isDisabledView
  );
};

export const getTemplateTypeValueFromSpread = (spread: Spread) => {
  return spread.financialTemplate.type;
};

export const returnInputType = (label: string) => {
  if (
    [
      "Auditor Name",
      "Customer Name",
      "Customer Name 2",
      "Cocunut",
      "Local Customer Number",
      "Unit",
      "naceCodeConfig",
      "NACE Code",
      "GICS Industry",
      "GICS Industry Sector",
      "Number of Months in Reported Period",
      "Input Currency",
      "Name of the Analyst",
      "Balance Sheet Date (End Date)",
      "Preparation Date of the Analysis",
      "Cocunut",
      "NACE Code",
      "Number of Months in Reported Period"
    ].includes(label)
  ) {
    return "text";
  } else {
    return "select";
  }
};

export const isNumeric = (label: string) => {
  return [
    "Cocunut",
    "NACE Code",
    "Number of Months in Reported Period"
  ].includes(label);
};
export const isDate = (label: string) => {
  return [
    "Balance Sheet Date (End Date)",
    "Preparation Date of the Analysis"
  ].includes(label);
};

export const transformRawConfigIntoMetadataInput = (
  rawConfig: RawMetadataConfig,
  isDbs?: boolean
): MetadataInput[] => {
  return map<any, any>((configField: RawMetadataConfigItem): MetadataInput => {
    const {
      isRequiredOnComplete,
      isRequiredOnUpload,
      label,
      values,
      isDisabledView,
      isPerPeriod,
      maxLength,
      maxValue,
      displayMetadataId
    } = configField;

    const formKey =
      label === "Source Currency" ? "currency" : toCamelCase(label);
    const options = values
      ? getDropdownOptionsFromMetadataValues(values)
      : getDropdownOptionsFromLocalFile(formKey, isDbs);
    const defaultValue =
      configField.defaultValue &&
      getDropdownOptionFromMetadataValue(configField.defaultValue);

    const inputType = returnInputType(label);
    const isNumericType = isNumeric(label);
    const isDateType = isDate(label);

    const metadataInput: MetadataInput = {
      options,
      isRequiredOnUpload,
      isRequiredOnComplete,
      label,
      formKey,
      inputType,
      isDisabledView,
      isPerPeriod,
      isNumericType,
      isDateType,
      maxLength,
      maxValue,
      displayMetadataId
    };
    if (defaultValue) {
      return { ...metadataInput, defaultValue };
    }
    return metadataInput;
  }, rawConfig);
};

export const getMetadataInputOnAboutPage = (rawConfig, isDbs) => {
  const filteredConfig = getMetadataFieldsOnAboutPage(rawConfig);
  const transformedConfig = transformRawConfigIntoMetadataInput(
    filteredConfig,
    isDbs
  );
  return ObjectToArray(transformedConfig);
};

export const getMetadataInputOnUploadForm = pipe(
  getMetadataFieldsOnAUploadForm,
  transformRawConfigIntoMetadataInput,
  ObjectToArray
);

export const transformRawMetadataConfigIntoFrontEndConfig = (
  rawConfig: RawMetadataConfig,
  isDbs?: boolean
): MetadataFrontEndConfig => {
  return {
    aboutPage: getMetadataInputOnAboutPage(rawConfig, isDbs),
    uploadForm: getMetadataInputOnUploadForm(rawConfig),
    rawConfig
  };
};



// WEBPACK FOOTER //
// ./src/metadata/helpers.ts